import { LocalDb } from "../db/Database";
import { ActionManager } from '../ActionManager';
import { Markdown } from "./Markdown";
import {MessageBar, MessageBarAction} from "../DomMenu";
import {MruList, SyncedMruList} from "./MruList";
import {IBlobStore, IDocument, IDocumentCommit} from "../IMemoRemote";
import {CallableDisposableStack, observable, StackDisposable} from "../lib/signal";
import {slot} from "../lib/slot";
import {Resolver} from "../core/Resolver";

@StackDisposable.Mixin
export class UiContext implements StackDisposable {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    public static instance : UiContext = new UiContext();

    public readonly resolver = new Resolver();

    public readonly mru: {[key: string]: MruList} = {};

    public readonly blobStore: IBlobStore;
    @observable
    public settingsDoc : IDocument;

    constructor(
        public readonly markdown? : Markdown,
        public readonly db? : LocalDb,
        public readonly sel? : ActionManager
    ) {
        this.blobStore = db;
    }

    /**
     * logs something with log type='log'
     * @param message
     * @param params
     */
    log(message:string, ...params: any) {
        this.log_('log', null, message, undefined, ...params);
    }

    /**
     * logs an error and also shows it as a toast message on the screen
     * @param message
     * @param error
     */
    logError(message: string, error?: Error) {
        if (error) this.toast("error", `${message}`, 5000, [], error);
        else this.toast("error", `${message}`, 5000, []);
    }

    /**
     * logs something with given log type
     * @param type
     * @param message
     * @param actions
     * @param params
     */
    log_(type: string, context: any, message: string, actions?: MessageBarAction[], ...params: any) {
        if (type === 'error')
            console.warn('ERR:', message, ...params);
        else
            console.info(message, ...params);
    }

    /**
     * displays a toast message on the screen, and also logs the given message
     * @param type
     * @param message
     * @param timeout
     * @param actions
     * @param params
     */
    toast(type: string, message: string, timeout?: number, actions?: MessageBarAction[], ...params: any);
    toast(message: string, timeout?: number, actions?: MessageBarAction[], ...params: any);
    toast(type: string, message, timeout, actions, ...params: any) {
        if (!type) type = 'toast';
        if (typeof message !== 'string') { params.unshift(actions); actions = timeout; timeout = message; message = type; type = 'toast'; }
        this.toast_(type, null, message, timeout, actions, ...params);
    }

    toast_(type: string, context: any, message: string, timeout: number | undefined, actions: MessageBarAction[] | undefined, ...params: any) {
        if (!actions || !(actions instanceof Array)) actions = [];
        if (timeout === undefined) timeout = 5000;
        this.log_(type, null, message, actions, ...params);
        MessageBar.show(type, message, timeout, actions);
    }

    readMru(listName: string): (string | object)[] {
        const lst = this.mru[listName] ?? new SyncedMruList(listName, 0, null);
        return lst.read();
    }
    storeMru(listName: string, newItem: string | URL | object, maxItems: number, deleteItem: boolean = false, equalityHash?: (any) => string) {
        if (!(listName in this.mru)) this.mru[listName] = new SyncedMruList(listName, maxItems, equalityHash);
        if (newItem === null) return;
        if (deleteItem)
            this.mru[listName].delete(newItem);
        else
            this.mru[listName].store(newItem);
    }
    @slot('settingsDoc', ($: IDocument) => $.updated)
    onRemoteItemUpdate_settings({ items }: IDocumentCommit) {
        for(const item of items) {
            if (item.opt === 'mrulist' && typeof item.name === 'string' && item.name.startsWith('mrulist-')) {
                localStorage[item.name] = JSON.stringify(item.data);
                if (this.mru[item.name]) this.mru[item.name].changed.emit();
            }
        }
    }

    public static metrics: {[key: string]: (content: string) => void} = {};
    public static metric(key: string, content: any) {
        if (key in UiContext.metrics) {
            UiContext.metrics[key]?.(""+content);
        } else {
            UiContext.metrics[key] = null;
        }
    }

}
